import React, {useEffect, useMemo} from "react";
import { observer } from "mobx-react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import { RoundedButton } from "../../../../../elements/button";
import IconSelect from "../../../../../elements/v2/Selects/IconSelect/IconSelect";
import { useStore } from "../../../../../hooks";
import DynamicBreadcrumbs from "../../../../../elements/v2/DynamicBreadcrumbs/DynamicBreadcrumbs";
import Tabs2 from "../../../../../elements/v2/Tabs2/Tabs2";
import { MarketingTab, MarketingStatus, PermissionCodes } from "../../../../../utils/enums";
import Spinner from "../../../../../components/common/Spinner";
import { useMediaQueries } from "../../../../../utils/mediaQueries";
import {AuditLogs, Overview, Segments, Message, AdvancedSettings} from "./Tabs";
import {useUserCheckPermissions} from "../../../../../hooks/useUserCheckPermissions";
import {
  CustomGenericListStyleDesktop,
  MarketingContainer,
  MarketingWrapper, MarketingWrapperHeader, MessageActionsWrapper, SegmentsActionsWrapper,
  TabContainer, UploadFilesActionsWrapper,
  TabButtonContainer,
} from "./Marketing.styled";
import {
  AgentSelectionConfirmationModal,
  CampaignScheduleModal,
  ConfirmationModal, ConfirmPublishModal,
  GeneralMessageModal
} from "./Components/Modal";
import {AgentSelectionAlert, MissingSegmentAlert} from "./Components/Alert";

const Marketing = observer(() => {
  const { isMobile } = useMediaQueries();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {checkPermission} = useUserCheckPermissions();

  const { authStore, marketingStore } = useStore();

  useEffect(() => {
    authStore.fetchAgents();
  }, [authStore]);

  useEffect(() => {
    const marketingId = searchParams.get("id");

    if (!marketingId) {
      navigate("/v2/conversations");
    }
    marketingStore.init({
      customer: authStore.selectedAccount.id,
      domains: authStore.customer.domains,
      id: marketingId,
      isLoadAll: false,
      isLoadOne: true
    });
  }, []);

  const breadcrumbItems = useMemo(
    () => [
      { label: "Conversations", href: "/v2/conversations" },
      { label: "Marketing", href: "/v2/conversations" },
      { label: marketingStore.currentCampaign?.name || 'loading ...' },
    ],
    [marketingStore.currentCampaign?.name]
  );

  const hasSegmentsPermission = checkPermission(PermissionCodes.SEGMENTS, "UPDATE");
  const hasMessagePermission = checkPermission(PermissionCodes.MESSAGE, "UPDATE");
  const hasOverviewPermission = checkPermission(PermissionCodes.OVERVIEW, "UPDATE");
  const hasAdvancedSettingsPermission = checkPermission(PermissionCodes.ADVANCED_SETTINGS, "UPDATE");

  const tabs = useMemo(
    () => [
      {
        label: "Segments",
        disabled: !hasSegmentsPermission,
      },
      {
        label: "Message",
        disabled: !hasMessagePermission,
      },
      {
        label: "Overview",
        disabled: !hasOverviewPermission,
      },
      {
        label: "Advanced settings",
        disabled: !hasAdvancedSettingsPermission,
      },
      {
        label: "Audit logs",
        disabled: false,
      },
    ],
    [marketingStore.existingFileContacts, hasSegmentsPermission, hasMessagePermission, hasOverviewPermission, hasAdvancedSettingsPermission]
  );

  const actionsButtons = useMemo(() => (
    <>
      {marketingStore.currentCampaign
        && ![MarketingStatus.ACTIVE, MarketingStatus.CANCELLED, MarketingStatus.MANUAL]
          .includes(marketingStore.currentCampaign.status)
        && checkPermission(PermissionCodes.CREATE_NEW, "CREATE") && (
        <>
          <RoundedButton
            dataTestId="save-button"
            width="80px"
            height="40px"
            fontSize="0.875rem"
            kind={"publish"}
            onClick={() => {
              void marketingStore.saveCampaign({});
            }}>
            Save
          </RoundedButton>
          <RoundedButton
            dataTestId="publish-button"
            width="65px"
            height="40px"
            fontSize="0.875rem"
            kind={"blue"}
            onClick={() => {
              void marketingStore.publishCampaign({});
            }}>
            Publish
          </RoundedButton>
        </>
      )}
    </>
  ), [marketingStore.currentCampaign]);

  const actionsOptions = useMemo(() => [
    {
      name: "Cancel",
      title: "Cancel",
      icon: "cancelSquare",
      onClick: () => {
        void marketingStore.cancelCampaign(marketingStore.currentCampaign);
      },
      displayOnStatus: [MarketingStatus.ACTIVE],
    },
    {
      name: "Duplicate",
      title: "Duplicate Conversation",
      icon: "duplicateOutline",
      onClick: () => {
        void marketingStore.cloneCampaign(marketingStore.currentCampaign, true);
      },
      displayOnStatus: [
        MarketingStatus.DRAFT,
        MarketingStatus.ACTIVE,
        MarketingStatus.CANCELLED,
        MarketingStatus.MANUAL,
      ],
    },
  ], [marketingStore]);

  const filteredActionOptions = useMemo(() => {
    if (marketingStore.currentCampaign) {
      return actionsOptions.filter((action) => action.displayOnStatus.includes(marketingStore.currentCampaign.status));
    }
    return [];
  }, [actionsOptions, marketingStore.currentCampaign]);

  return (
    <>
      <GeneralMessageModal />
      <ConfirmationModal />
      <AgentSelectionConfirmationModal />
      <CampaignScheduleModal />
      <ConfirmPublishModal />
      <AgentSelectionAlert />
      <MissingSegmentAlert />

      <MarketingContainer isMobile={isMobile}>
        <DynamicBreadcrumbs items={breadcrumbItems} currentPath={pathname} />
        {marketingStore.isLoading && <Spinner dataTestId="spinner" />}

        {!marketingStore.isLoading && (
          <MarketingWrapper
            $isSegment={marketingStore.currentTab === MarketingTab.Segments}
            $rightPreview={marketingStore.showRightPreview}
          >
            <MarketingWrapperHeader>
              <TabContainer>
                <Tabs2
                  tabs={tabs}
                  onToggleTab={(tab) => marketingStore.onChangeTab(tab)}
                  defaultTab={marketingStore.currentTab}
                />
                <TabButtonContainer>
                  {marketingStore.currentTab === MarketingTab.Message && (
                    <MessageActionsWrapper>
                      {actionsButtons}
                    </MessageActionsWrapper>
                  )}
                  {marketingStore.currentTab === MarketingTab.Segments && marketingStore.currentTab === MarketingTab.Segments && hasSegmentsPermission && (
                    <SegmentsActionsWrapper>
                      {actionsButtons}
                    </SegmentsActionsWrapper>
                  )}
                  {marketingStore.currentTab === MarketingTab.Overview && (
                    <UploadFilesActionsWrapper>
                      <IconSelect
                        placeholder="Actions"
                        options={filteredActionOptions}
                      />
                      {actionsButtons}
                    </UploadFilesActionsWrapper>
                  )}
                  {marketingStore.currentTab === MarketingTab.AdvancedSettings && (
                    <SegmentsActionsWrapper className="advanced-settings">
                      {actionsButtons}
                    </SegmentsActionsWrapper>
                  )}
                </TabButtonContainer>
              </TabContainer>

              <Tabs2.Outlet show={marketingStore.currentTab === MarketingTab.Message}>
                <Message />
              </Tabs2.Outlet>

              <Tabs2.Outlet show={marketingStore.currentTab === MarketingTab.Segments && hasSegmentsPermission}>
                <Segments />
              </Tabs2.Outlet>

              <Tabs2.Outlet show={marketingStore.currentTab === MarketingTab.Overview}>
                <Overview />
              </Tabs2.Outlet>

              <Tabs2.Outlet
                show={marketingStore.currentTab === MarketingTab.AdvancedSettings}
              >
                <AdvancedSettings/>
              </Tabs2.Outlet>

              <Tabs2.Outlet show={marketingStore.currentTab === MarketingTab.AuditLogs}>
                <AuditLogs />
              </Tabs2.Outlet>
            </MarketingWrapperHeader>
          </MarketingWrapper>
        )}
        <CustomGenericListStyleDesktop />
      </MarketingContainer>
    </>
  );
});

export default Marketing;
